import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faLink, faLinkSlash, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import api from '../utils/api';

/**
 * Which CRM company a vendor belongs to, on their Vendors profile.
 *
 * Everybody who can see the vendor sees the company's name. Linking, changing
 * and opening it in the CRM are for people with CRM access, and the companies
 * offered are only ones the CRM already shows them (services/vendorCrmLink.js).
 */
const VendorCrmLink = ({ vendorId, crm, onChanged }) => {
    const navigate = useNavigate();
    const [picking, setPicking] = useState(false);
    const [q, setQ] = useState('');
    const [results, setResults] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!picking) return undefined;
        const timer = setTimeout(() => {
            api.get('/external-users/crm-companies', { params: { q: q || undefined, for: vendorId } })
                .then((res) => setResults(res.data))
                .catch((err) => setError(err.response?.data?.message || 'Could not load CRM companies.'));
        }, q ? 300 : 0);
        return () => clearTimeout(timer);
    }, [picking, q, vendorId]);

    const save = async (companyId) => {
        setBusy(true);
        setError('');
        try {
            await api.put(`/external-users/${vendorId}/crm-company`, { companyId });
            setPicking(false);
            setQ('');
            onChanged();
        } catch (err) {
            setError(err.response?.data?.message || 'Could not save the link.');
        } finally {
            setBusy(false);
        }
    };

    const linked = crm?.linked;
    if (!linked && !crm?.canLink) return null;

    const option = (c, tag) => (
        <li key={`${tag}-${c.id}`}>
            <button type="button" className="exu-crm-option" disabled={busy} onClick={() => save(c.id)}>
                <strong>{c.name}</strong>
                <span>{[c.domain, c.type, c.city].filter(Boolean).join(' · ')}</span>
                {tag === 'suggested' && <em>Matches their email</em>}
            </button>
        </li>
    );

    return (
        <div className="exu-crm">
            <h5><FontAwesomeIcon icon={faBuilding} /> CRM company</h5>
            {linked ? (
                <div className="exu-membership">
                    <div>
                        <strong>{linked.name || 'A CRM company'}</strong>
                        <span>
                            {linked.domain ? `${linked.domain} · ` : ''}
                            Linked{linked.linkedBy ? ` by ${linked.linkedBy}` : ''}
                            {linked.linkedAt ? ` on ${new Date(linked.linkedAt).toLocaleDateString('en-GB')}` : ''}
                        </span>
                        <div className="exu-crm-actions">
                            {linked.link && <button className="exu-btn" onClick={() => navigate(linked.link)}>Open in CRM</button>}
                            {crm.canLink && <button className="exu-btn" onClick={() => setPicking(!picking)}>Change</button>}
                            {crm.canLink && (
                                <button className="exu-btn" disabled={busy} onClick={() => save(null)}>
                                    <FontAwesomeIcon icon={faLinkSlash} /> Unlink
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                !picking && (
                    <button className="exu-btn" onClick={() => setPicking(true)}>
                        <FontAwesomeIcon icon={faLink} /> Link to a CRM company
                    </button>
                )
            )}

            {picking && (
                <div className="exu-crm-picker">
                    <div className="exu-search">
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                        <input autoFocus value={q} placeholder="Search CRM companies by name or domain"
                            onChange={(e) => setQ(e.target.value)} />
                    </div>
                    {!results && !error && <p className="exu-note">Loading…</p>}
                    {results && (
                        <ul>
                            {!q && results.suggested.map((c) => option(c, 'suggested'))}
                            {results.companies
                                .filter((c) => q || !results.suggested.some((s) => s.id === c.id))
                                .map((c) => option(c, 'match'))}
                            {!results.companies.length && !results.suggested.length && (
                                <li className="exu-note">No CRM company matches. Add it in the CRM first.</li>
                            )}
                        </ul>
                    )}
                    <button className="exu-btn" onClick={() => { setPicking(false); setQ(''); }}>Cancel</button>
                </div>
            )}
            {error && <p className="exu-note exu-error">{error}</p>}
        </div>
    );
};

export default VendorCrmLink;
